import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import { ApiClientError, type WorkspaceRequestPlanResponse } from "@/lib/types";
import { Button } from "@/components/Button";
import { StatePanel } from "@/components/StatePanel";
import type { Locale, Messages } from "@/locale";
import { useLocale } from "@/locale/LocaleContext";
import ui from "@/components/ui.module.css";

type PlanSource = "customer" | "operator";

type Props = {
  requestId: string;
  source: PlanSource;
};

export function WorkspaceRequestPlanPanel({ requestId, source }: Props) {
  const { m, locale, fmt } = useLocale();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WorkspaceRequestPlanResponse | null>(null);
  const [retry, setRetry] = useState(0);
  const requestIdRef = useRef(requestId);
  const loadFailedRef = useRef(m.workspace.planLoadFailed);
  loadFailedRef.current = m.workspace.planLoadFailed;
  requestIdRef.current = requestId;

  useEffect(() => {
    setOpen(false);
    setLoading(false);
    setError(null);
    setData(null);
    setRetry(0);
  }, [requestId]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const ac = new AbortController();
    const id = requestId;
    setLoading(true);
    setError(null);
    const fetchPlan =
      source === "operator"
        ? api.operatorWorkspaceRequestPlan(id, ac.signal)
        : api.workspaceRequestPlan(id, ac.signal);
    fetchPlan
      .then((res) => {
        if (!alive || requestIdRef.current !== id) return;
        setData(res);
        setError(null);
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted || requestIdRef.current !== id) return;
        setData(null);
        setError(e instanceof ApiClientError ? e.message : loadFailedRef.current);
      })
      .finally(() => {
        if (alive && requestIdRef.current === id) setLoading(false);
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [open, requestId, source, retry]);

  return (
    <div style={{ marginTop: "0.55rem" }}>
      <div className={ui.actions} style={{ justifyContent: "flex-start" }}>
        <Button
          type="button"
          variant="secondary"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? m.workspace.planHide : m.workspace.planShow}
        </Button>
      </div>

      {open ? (
        <div
          className={`${ui.panel} ${ui.panelInfo}`}
          style={{ marginTop: "0.55rem", display: "grid", gap: "0.65rem" }}
          aria-busy={loading || undefined}
        >
          <h3 className={ui.panelTitle} style={{ fontSize: "1rem" }}>
            {m.workspace.planTitle}
          </h3>
          <p className={ui.panelBody}>{m.workspace.planMode}</p>
          <p className={ui.meta}>{m.workspace.planNoExecution}</p>

          {loading ? <StatePanel kind="pending" title={m.common.loading} /> : null}

          {error ? (
            <div role="alert">
              <p className={ui.fieldError}>{error}</p>
              <Button type="button" variant="secondary" onClick={() => setRetry((n) => n + 1)}>
                {m.workspace.planRetry}
              </Button>
            </div>
          ) : null}

          {!loading && !error && data ? (
            <PlanBody data={data} locale={locale} fmt={fmt} m={m} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PlanBody({
  data,
  locale,
  fmt,
  m,
}: {
  data: WorkspaceRequestPlanResponse;
  locale: Locale;
  fmt: (template: string, vars?: Record<string, string | number>) => string;
  m: Messages;
}) {
  const { plan } = data;
  const sourceLabel = data.persisted
    ? m.workspace.planSourcePersisted
    : m.workspace.planSourcePreview;

  return (
    <>
      <p className={ui.meta}>
        <strong>{m.workspace.planSourceLabel}:</strong> {sourceLabel}
        {data.persisted && data.created_at
          ? ` · ${fmt(m.workspace.planSnapshotAt, { when: formatWhen(data.created_at, locale) })}`
          : null}
      </p>
      <p className={ui.meta}>
        <strong>{m.workspace.planRequestState}:</strong> {lifecycleLabel(m, data.request_state)}
        {" · "}
        <strong>{m.workspace.profile}:</strong> {lifecycleLabel(m, plan.profile)}
      </p>

      <p className={ui.meta}>
        <strong>{m.workspace.toolsLabel}:</strong>{" "}
        {plan.requested_tools.length
          ? plan.requested_tools.map((tool) => lifecycleLabel(m, tool)).join(", ")
          : "—"}
      </p>

      <div>
        <p className={ui.meta} style={{ marginBottom: "0.25rem" }}>
          <strong>{m.workspace.planPrerequisites}</strong>
        </p>
        {plan.blockers.length === 0 ? (
          <p className={ui.meta}>{m.workspace.planPrerequisitesNone}</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.15rem" }}>
            {plan.blockers.map((blocker) => (
              <li key={blocker} className={ui.meta}>
                {lifecycleLabel(m, blocker)}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <p className={ui.meta} style={{ marginBottom: "0.25rem" }}>
          <strong>{m.workspace.planSteps}</strong>
        </p>
        {plan.steps.length === 0 ? (
          <p className={ui.meta}>{m.workspace.planStepsNone}</p>
        ) : (
          <ol style={{ margin: 0, paddingLeft: "1.15rem" }}>
            {plan.steps.map((step) => (
              <li key={step.action} className={ui.meta}>
                {lifecycleLabel(m, step.action)}
                {" · "}
                {lifecycleLabel(m, step.state)}
              </li>
            ))}
          </ol>
        )}
      </div>

      <div>
        <p className={ui.meta} style={{ marginBottom: "0.25rem" }}>
          <strong>{m.workspace.planCapabilities}</strong>
        </p>
        <ul style={{ margin: 0, paddingLeft: "1.15rem" }}>
          <li className={ui.meta}>
            {m.workspace.planCapProvisioning}: {m.workspace.planCapDisabled}
          </li>
          <li className={ui.meta}>
            {m.workspace.planCapAiExecution}: {m.workspace.planCapDisabled}
          </li>
          <li className={ui.meta}>
            {m.workspace.planCapArchiveRestore}: {m.workspace.planCapDisabled}
          </li>
        </ul>
      </div>

      <p className={ui.meta}>
        {m.workspace.planExecutableLabel}: {m.workspace.planExecutableNo}
        {" · "}
        {m.workspace.planModeLabel}: {lifecycleLabel(m, plan.mode)}
      </p>
    </>
  );
}
